import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Megaphone, AlertCircle, Sparkles, Radio, Crosshair, Clock,
  TrendingUp, Users, Ticket, Check
} from "lucide-react";
import { FootTrafficAuditForm } from "../../components/FootTrafficAuditForm";
import { SEO } from "../../components/SEO";

const EntertainmentSolutionPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="solution-entertainment-page">
      <SEO
        title="SmartReach™ for Events & Entertainment"
        description="Sell tickets faster and lift event attendance with SmartReach™ — radio + digital push, geo-targeted reach, and urgency messaging that drives action."
        path="/solutions/entertainment"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Megaphone size={16} /> Events & Entertainment
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Sell Out Faster With <span className="text-gradient-orange">The Right Local Audience</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            SmartReach&trade; for events drives ticket sales and attendance — combining radio's emotional
            reach with digital's precision and urgency.
          </p>
          <button onClick={() => navigate("/contact")} className="btn-primary text-lg">
            Drive Attendance <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Event Challenges</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Event Awareness", desc: "Reaching the right local audience in time, with credibility, before they've already planned their weekend." },
              { title: "Attendance Uncertainty", desc: "Slow ticket sales, no-shows, and last-minute scrambles to fill seats — a stress every promoter knows." }
            ].map((c) => (
              <div key={c.title} className="bg-gradient-to-br from-red-50 to-white p-6 rounded-2xl border border-red-100">
                <AlertCircle className="text-red-500 mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{c.title}</h3>
                <p className="text-gray-medium text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-white px-3 py-1 rounded-full shadow-sm">
              <Sparkles size={14} /> SmartReach Solution
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The Event <span className="text-gradient-orange">Sell-Through Stack</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Radio, title: "Radio + Digital Push", desc: "Radio creates emotional reach and credibility. Digital retargets engaged listeners with ticket links and reminders." },
              { icon: Crosshair, title: "Geo Targeting", desc: "Reach audiences within driving distance of the venue — and exclude already-purchased ticket holders." },
              { icon: Clock, title: "Urgency Messaging", desc: "Countdown creatives, low-inventory alerts, and last-chance offers drive faster conversions." }
            ].map((s) => (
              <div key={s.title} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <s.icon className="text-orange-primary" size={24} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{s.title}</h3>
                <p className="text-gray-medium text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Use Cases</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Concert / Festival Launch",
                desc: "Multi-week radio + digital ramp targeting music lovers within driving distance. Urgency creatives in the final 7 days drive last-mile conversions.",
                bullets: ["Multi-week awareness", "Geo + interest targeting", "Final-week urgency push"]
              },
              {
                title: "Movie / Show Premiere",
                desc: "Opening-weekend push combining radio buzz, digital trailers, and time-bound offers to maximize attendance in the first 72 hours.",
                bullets: ["Trailer-driven creatives", "Time-bound promotions", "Cross-channel frequency"]
              }
            ].map((u) => (
              <div key={u.title} className="bg-gradient-to-br from-orange-light to-white p-8 rounded-2xl border border-orange-primary/10 card-hover">
                <Ticket className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-2xl text-gray-dark mb-2">{u.title}</h3>
                <p className="text-gray-medium mb-4">{u.desc}</p>
                <div className="space-y-2 pt-4 border-t border-orange-primary/10">
                  {u.bullets.map((b) => (
                    <div key={b} className="flex items-center gap-2">
                      <Check size={16} className="text-orange-primary flex-shrink-0" />
                      <span className="text-sm text-gray-dark">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Results</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Users, title: "Higher Attendance", desc: "More seats filled, fewer no-shows, stronger event-day energy." },
              { icon: TrendingUp, title: "Faster Ticket Sales", desc: "Urgency-driven creatives accelerate sell-through and reduce last-minute panic." },
              { icon: Ticket, title: "Better Margins", desc: "Higher attendance and predictable sell-through improve event ROI and reduce risk." }
            ].map((r) => (
              <div key={r.title} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <r.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{r.title}</h3>
                <p className="text-gray-medium text-sm">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FootTrafficAuditForm
        title="Sell out your next event"
        subtitle="Tell us about your event. We'll build a SmartReach™ plan that drives tickets and attendance."
        variant="dark"
      />
    </div>
  );
};

export default EntertainmentSolutionPage;
